import { Component, Output, EventEmitter, signal, inject, OnInit, Injectable } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, AbstractControl, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { NgSelectModule } from '@ng-select/ng-select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PreventLeadingSpaceDirective } from '@app/shared/components/utils/prevent-leading-space.directive';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ApiService } from '@app/core/services/API/api-service';
import { ToastrService } from 'ngx-toastr';
import { Spinner } from '@app/shared/components/spinner/spinner'; // Custom spinner component
@Injectable()

export class CustomDateAdapter extends DateAdapter<Date> {
  override format(date: Date, displayFormat: Object): string {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
  
  // Implement other required methods as pass-throughs
  override parse(value: any): Date | null {
    return value ? new Date(value) : null;
  }
  override getDate(date: Date): number {
    return date.getDate();
  }
  override getMonth(date: Date): number {
    return date.getMonth();
  }
  override getYear(date: Date): number {
    return date.getFullYear();
  }
  override getDayOfWeek(date: Date): number {
    return date.getDay();
  }
  override getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    const months = [];
    for (let i = 0; i < 12; i++) {
      const date = new Date(2000, i, 1);
      months.push(date.toLocaleDateString('en-US', { month: style }));
    }
    return months;
  }
  override getDateNames(): string[] {
    const days = [];
    for (let i = 1; i <= 31; i++) {
      days.push(i.toString());
    }
    return days;
  }
  override getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(2000, 0, 2 + i); // Start from Sunday
      days.push(date.toLocaleDateString('en-US', { weekday: style }));
    }
    return days;
  }
  override getYearName(date: Date): string {
    return date.getFullYear().toString();
  }
  override getFirstDayOfWeek(): number {
    return 0; // Sunday
  }
  override getNumDaysInMonth(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  }
  override addCalendarYears(date: Date, years: number): Date {
    return new Date(date.getFullYear() + years, date.getMonth(), date.getDate());
  }
  override addCalendarMonths(date: Date, months: number): Date {
    return new Date(date.getFullYear(), date.getMonth() + months, date.getDate());
  }
  override addCalendarDays(date: Date, days: number): Date {
    return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
  }
  override toIso8601(date: Date): string {
    return date.toISOString();
  }
  override isDateInstance(obj: any): boolean {
    return obj instanceof Date;
  }
  override isValid(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }
  override invalid(): Date {
    return new Date(NaN);
  }
  override clone(date: Date): Date {
    return new Date(date.getTime());
  }
  override createDate(year: number, month: number, date: number): Date {
    return new Date(year, month, date);
  }
  override today(): Date {
    return new Date();
  }
}

// Custom date format configuration
export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'MM/DD/YYYY',
  },
  display: {
    dateInput: 'MMMM D, YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'MMMM D, YYYY',
    monthYearA11yLabel: 'MMM Y',
  },
};
export interface Person {
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phone: string;
  dateOfBirth: Date | null;
  jobInfo: string;
  company: string;
  jobRoleID: string;
  timezone: string;
  country: string;
  homeTeamID: string;
  actualTeamID: string;
  managerID: string;
  accesslevel: string;
  accesslevelType: string;
  utype: string;
  address: string;
  isActive: boolean;
  description?: string;
  position?: string;
}


@Component({
  selector: 'app-add-people-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    NgSelectModule,
    MatProgressSpinnerModule,
    PreventLeadingSpaceDirective,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    Spinner
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' }
  ],
  templateUrl: './add-people-form.html',
  styleUrl: './add-people-form.scss'
})
export class AddPeopleForm implements OnInit {
  @Output() personSubmitted = new EventEmitter<Person>();
  @Output() close = new EventEmitter<void>();

  // Loading state
  isLoading = false;

  // Max date for date picker (today - no future dates)
  maxDate = new Date();

  // Form Controls for validation
  firstNameControl = new FormControl('', [Validators.required]);
  lastNameControl = new FormControl('', [Validators.required]);
  emailControl = new FormControl('', [Validators.required, Validators.email, Validators.maxLength(100)]);
  phoneControl = new FormControl('', [
    Validators.pattern(/^\+?[1-9]\d{9,14}$/),
    Validators.minLength(10),
    Validators.maxLength(15)
  ]);
  dobControl = new FormControl<Date | null>(null, [this.validateAge]);
  companyControl = new FormControl(null);
  jobTitleControl = new FormControl('', [Validators.required, Validators.minLength(2)]);
  jobRoleControl = new FormControl(null, [Validators.required]);
  timezoneControl = new FormControl(null, [Validators.required]);
  countryControl = new FormControl(null);
  homeTeamControl = new FormControl(null, [Validators.required]);
  actualTeamControl = new FormControl(null, [Validators.required]);
  managerControl = new FormControl<string | null>(null, [Validators.required]);
  accessLevelControl = new FormControl(null, [Validators.required]);
  departmentControl = new FormControl(null, [Validators.required]);
  positionControl = new FormControl({value: null, disabled: true});
  accessLevelTypeControl = new FormControl<string | null>(null, [Validators.required]);
  addressControl = new FormControl('', [Validators.maxLength(250)]);
  isActiveControl = new FormControl(true);
  descriptionControl = new FormControl('', [Validators.maxLength(500)]);

  // Data signals
  jobRoles = signal<any[]>([]);
  homeTeams = signal<any[]>([]);
  managers = signal<any[]>([]);
  companies = signal<any[]>([]);
  departments = signal<any[]>([]);
  positions = signal<any[]>([]);
  isLoadingPositions = signal<boolean>(false);

  // Static options
  timezoneOptions: any[] = [];

  countryOptions: any[] = [
    { name: 'Canada', code: 'CA' },
    { name: 'India', code: 'IN' }
  ];

  accessLevelOptions :any[] = [ ];

  readonly accessLevelTypeOptions: { label: string; value: string }[] = [
    { label: 'Member', value: 'Member' },
    { label: 'Manager', value: 'Manager' },
  ];

  // Services
  private apiService = inject(ApiService);
  private toastr = inject(ToastrService);

  // Store server error message for phone field
  private phoneServerErrorMessage = '';

  ngOnInit(): void {
    this.loadJobRoles();
    this.loadTeams();
    this.loadManagers();
    this.loadAccessLevels();
    this.loadTimezones();
    this.loadCompanies();
    this.loadDepartments();

    // Default Actual Team to Home Team when Home Team changes
    // (keeps sync unless the user has already picked a different Actual Team)
    let previousHomeTeamId: string | null = null;
    this.homeTeamControl.valueChanges.subscribe((homeTeamId) => {
      const currentActual = this.actualTeamControl.value;
      if (!currentActual || currentActual === previousHomeTeamId) {
        this.actualTeamControl.setValue(homeTeamId ?? null);
      }
      previousHomeTeamId = homeTeamId ?? null;
    });

    // Watch for department changes to load positions
    this.departmentControl.valueChanges.subscribe((department) => {
      if (department) {
        this.positionControl.disable(); // Disable while loading
        // Find the department object to get department_label
        const deptObj = this.departments().find(d => d._id === department || d.department === department);
        const departmentLabel = deptObj?.department_label || department;
        this.loadPositions(departmentLabel);
      } else {
        this.positions.set([]);
        this.positionControl.setValue(null);
        this.positionControl.disable(); // Disable when no department selected
        this.positionControl.markAsUntouched(); // Reset validation state
      }
    });
  }

  onClose(): void {
    this.close.emit();
  }
  toggleActiveStatus(): void {
    this.isActiveControl.setValue(!this.isActiveControl.value);
  }
  isFormValid(): boolean {
    return this.firstNameControl.valid &&
           this.lastNameControl.valid &&
           this.emailControl.valid && 
           this.phoneControl.valid && 
           this.dobControl.valid && 
           this.jobTitleControl.valid && 
           this.jobRoleControl.valid &&
           this.timezoneControl.valid && 
           this.homeTeamControl.valid &&
           this.actualTeamControl.valid &&
           this.managerControl.valid &&
           this.accessLevelControl.valid &&
           this.departmentControl.valid &&
           this.accessLevelTypeControl.valid;
  }

  // Custom validator for age (must be at least 16 years old)
  private validateAge(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Date of birth is optional
    }
    
    const today = new Date();
    const birthDate = new Date(control.value);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    if (age < 16) {
      return { 'minAge': { value: control.value, minAge: 16 } };
    }
    
    if (age > 100) {
      return { 'maxAge': { value: control.value, maxAge: 100 } };
    }
    
    return null;
  }

  private loadJobRoles(): void {
    this.apiService.getJobRole().subscribe({
      next: (res: any) => {
        const jobRoles = res.data || res;
        this.jobRoles.set(Array.isArray(jobRoles) ? jobRoles : []);
      },
      error: (error) => {
        console.error('Error loading job roles:', error);
        this.jobRoles.set([]);
      }
    });
  }

  private loadTeams(): void {
    this.apiService.getHomeTeamList().subscribe({
      next: (res: any) => {
        const teams = res.data || res;
        const teamsArray = Array.isArray(teams) ? teams : [];
        const mappedTeams = teamsArray.map((team: any) => ({
          ...team,
          teamName: team.tname || team.teamName || team.name || ''
        }));
        this.homeTeams.set(mappedTeams);
      },
      error: (error) => {
        console.error('Error loading teams:', error);
        this.homeTeams.set([]);
      }
    });
  }

  private loadAccessLevels(): void {
    this.apiService.getAccessLevelList().subscribe({
      next: (res: any) => {
        const accessLevels = res.data || res;
        this.accessLevelOptions = Array.isArray(accessLevels) ? accessLevels : [];
      },
      error: (error) => {
        console.error('Error loading access levels:', error);
        this.accessLevelOptions = [];
      }
    });

  }
  
  private loadTimezones(): void {
    this.apiService.getTimeZoneList().subscribe({
      next: (res: any) => {
        const timezones = res.data || res;
        this.timezoneOptions = Array.isArray(timezones) ? timezones : [];
      }
      ,
      error: (error) => { 
        console.error('Error loading timezones:', error);
        this.timezoneOptions = [];
      }
    });
  }

  private loadManagers(): void {
    this.apiService.getManagerData().subscribe({
      next: (res: any) => {
        const users = res.data || res;
        // Filter users who can be managers (you might want to add a role check here)
        this.managers.set(Array.isArray(users) ? users : []);
      },
      error: (error) => {
        console.error('Error loading managers:', error);
        this.managers.set([]);
      }
    });
  }
  
  private loadCompanies(): void {
    this.apiService.getCompanyList().subscribe({
      next: (res: any) => {
        const companies = res.data || res;
        this.companies.set(Array.isArray(companies) ? companies : []);
      },
      error: (error) => {
        console.error('Error loading companies:', error);
        this.companies.set([]);
      }
    });
  }

  private loadDepartments(): void {
    this.apiService.getDepartmentNames().subscribe({
      next: (res: any) => {
        const departments = res.data || res;
        this.departments.set(Array.isArray(departments) ? departments : []);
      },
      error: (error) => {
        console.error('Error loading departments:', error);
        this.departments.set([]);
      }
    });
  }

  private loadPositions(departmentLabel: string): void {
    this.positionControl.setValue(null); // Reset position when department changes
    this.positionControl.markAsUntouched(); // Reset validation state
    this.positions.set([]); // Clear positions while loading
    this.isLoadingPositions.set(true); // Set loading state
    this.positionControl.disable(); // Disable while loading
    this.apiService.getDepartmentPositions(departmentLabel).subscribe({
      next: (res: any) => {
        let positionsArray: any[] = [];
        
        // Handle the new response structure where positions are nested inside a 'position' property
        if (res.data && Array.isArray(res.data) && res.data.length > 0) {
          // Extract positions from the first item in the data array
          const departmentData = res.data[0];
          if (departmentData.position && Array.isArray(departmentData.position)) {
            positionsArray = departmentData.position.map((pos: any) => ({
              _id: pos._id || pos.name, // Use name as fallback for _id
              position: pos.name,
              memberIDs: pos.memberIDs || []
            }));
          }
        } else if (res.data && Array.isArray(res.data)) {
          // Fallback for old structure or direct array
          positionsArray = res.data;
        } else if (Array.isArray(res)) {
          // Fallback for direct array response
          positionsArray = res;
        }
        this.positions.set(positionsArray);
        this.isLoadingPositions.set(false); // Loading complete
        // Enable position control only if positions are loaded and department is still selected
        if (this.departmentControl.value && positionsArray.length > 0) {
          this.positionControl.enable();
        } else {
          this.positionControl.disable();
        }
      },
      error: (error) => {
        console.error('Error loading positions:', error);
        this.positions.set([]);
        this.isLoadingPositions.set(false); // Loading complete even on error
        this.positionControl.disable(); // Keep disabled on error
      }
    });
  }

  async onSubmit(): Promise<void> {
    if (!this.isFormValid() || this.isLoading) return;

    this.isLoading = true;

    try {
      const firstName = (this.firstNameControl.value || '').trim();
      const lastName = (this.lastNameControl.value || '').trim();
      const name = `${firstName} ${lastName}`.trim();

      const personData: Person = {
        firstName,
        lastName,
        name,
        email: this.emailControl.value || '',
        phone: this.phoneControl.value || '',
        dateOfBirth: this.dobControl.value,
        jobInfo: this.jobTitleControl.value || '',
        company: this.companyControl.value || '',
        jobRoleID: this.jobRoleControl.value || '',
        timezone: this.timezoneControl.value || '',
        country: this.getCountryName(this.countryControl.value || ''),
        homeTeamID: this.homeTeamControl.value || '',
        actualTeamID: this.actualTeamControl.value || '',
        managerID: this.managerControl.value || '',
        accesslevel: this.accessLevelControl.value || '',
        accesslevelType: this.accessLevelTypeControl.value || '',
        address: this.addressControl.value || '',
        isActive: this.isActiveControl.value || false,
        utype: 'user',
        description: this.descriptionControl.value || '',
        position: this.positionControl.value || ''
      };

      // Call API to create person
      const result = await this.apiService.CreatePeople(personData).toPromise();
      // Update personData with the response data if available
      const finalPersonData = {
        ...personData,
        _id: result.data?._id || '',
        createdAt: result.data?.createdAt || new Date().toISOString(),
        updatedAt: result.data?.updatedAt || new Date().toISOString(),
        dateOfBirth: result.data?.dateOfBirth || personData.dateOfBirth
      };
      
      this.personSubmitted.emit(finalPersonData);
      this.toastr.success(result.message || 'Person created successfully!');
      this.onClose();
    } catch (error: any) {
      console.error('Error creating person:', error);
      
      // Extract backend error message from HTTP error response
      // Angular HttpClient wraps errors, so check error.error for backend response
      let errorMessage = 'Failed to create person. Please try again.';
      
      if (error?.error?.message) {
        // Backend error message (e.g., "Email already exists.")
        errorMessage = error.error.message;
      } else if (error?.error?.error?.message) {
        // Sometimes nested in error.error.error
        errorMessage = error.error.error.message;
      } else if (error?.message) {
        // Fallback to HTTP error message
        errorMessage = error.message;
      }
      
      // Show backend error message in toastr
      this.toastr.error(errorMessage);
      
      // Highlight email field if error is related to email
      if (errorMessage.toLowerCase().includes('email')) {
        // Set custom error on email control to highlight the field
        this.emailControl.setErrors({ 'serverError': true });
        this.emailControl.markAsTouched();
        
        // Focus the email input field and scroll into view
        setTimeout(() => {
          const emailInput = document.querySelector('input[name="email"]') as HTMLInputElement;
          if (emailInput) {
            emailInput.focus();
            emailInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
      
      // Highlight phone field if error is related to phone
      if (errorMessage.toLowerCase().includes('phone') || errorMessage.toLowerCase().includes('mobile')) {
        // Store the error message for display
        this.phoneServerErrorMessage = errorMessage;
        
        // Set custom error on phone control to highlight the field
        this.phoneControl.setErrors({ 'serverError': true });
        this.phoneControl.markAsTouched();
        
        // Focus the phone input field and scroll into view
        setTimeout(() => {
          const phoneInput = document.querySelector('input[name="phone"]') as HTMLInputElement;
          if (phoneInput) {
            phoneInput.focus();
            phoneInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
      
      // Modal stays open - don't call onClose() on error
    } finally {
      this.isLoading = false;
    }
  }

  // Get phone error message for display
  getPhoneErrorMessage(): string {
    return this.phoneServerErrorMessage || 'Phone number is invalid. Please use a different phone number.';
  }

 

  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getCountryName(countryCode: string): string {
    const country = this.countryOptions.find(c => c.code === countryCode);
    return country ? country.name : countryCode;
  }
}
